import React, { useEffect, useState } from 'react';
import pickupOrderRemote from '../../../api/pickupOrderRemote';
import MaterialTable from 'material-table';
import { Paper } from '@material-ui/core';
import DotIcon from '@material-ui/icons/Lens';
import Moment from 'moment';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';

import { truckStatusTime } from '../../../utils/truckStatusTime';
import PickupOrderFwd from '../../../models/PickupOrderFwd';
import commonStyles from '../../../components/common/common.module.css';

interface FwdGoAwbsTableProps {
  pickupOrders: PickupOrderFwd[];
  onSelectionChange: (selectedRows: any[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    green: { color: 'green', fontSize: '14px' },
    red: { color: 'red', fontSize: '14px' },
    yellow: { color: 'orange', fontSize: '14px' },
    checkboxPrimaryScope: {
      '& [class*="MTableToolbar-root"]': {
        minHeight: 'auto',
        paddingBottom: '20px',
        paddingRight: '24px',
      },
      '& [class*="MTableToolbar-title"] h3': {
        margin: 0,
      },
      '& .MuiCheckbox-colorSecondary': {
        color: 'white',
      },
      '& .MuiCheckbox-colorSecondary.Mui-checked': {
        color: 'white',
      },
      '& .MuiCheckbox-root': {
        padding: '6px',
      },
      '& .MuiCheckbox-root .MuiSvgIcon-root': {
        fontSize: '18px',
      },
      '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before': {
        borderBottom: 'none',
      },
    },
  })
);

export default function FwdGoAwbsTable({ pickupOrders, onSelectionChange }: FwdGoAwbsTableProps) {
  const classes = useStyles();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchAwbs() {
      setLoading(true);
      const awbs: any[] = [];
      if (!pickupOrders || pickupOrders.length === 0) {
        setData(awbs);
        setLoading(false);
        return;
      }
      for (const po of pickupOrders) {
        try {
          const res: any = await pickupOrderRemote.getPickupOrder(po.number, 'go');
          if (res && res.result && res.result.list) {
            res.result.list.forEach((awb: any) => {
              awbs.push({ ...awb, go_number: po.number, parentOrder: { ...res.result, ...po } });
            });
          }
        } catch (e) {
          console.error("Failed to fetch GO order awbs", e);
        }
      }
      setData(awbs);
      setLoading(false);
    }
    
    fetchAwbs();
  }, [pickupOrders]);

  return (
    <div className={commonStyles.wrapper}>
      <div className={ classes.checkboxPrimaryScope }>
        <MaterialTable
          components={{ Container: props => <Paper {...props} elevation={0} className={commonStyles.paperTable} /> }}
          data={data}
          columns={[
            { title: 'Order #', field: 'go_number', customFilterAndSearch: (term: string, rowData: any) => rowData.go_number?.toLowerCase().includes(term.toLowerCase()) },
            { title: 'Airwaybill #', field: 'number', render: (rowData: any) => `${rowData.code}-${rowData.number}`, customFilterAndSearch: (term: string, rowData: any) => `${rowData.code}-${rowData.number}`.toLowerCase().includes(term.toLowerCase()) },
            { title: 'Pcs', field: 'pieces' },
            { title: 'Wgt', field: 'weight' },
            { title: 'Status', field: 'converted_to_po', render: (rowData: any) => rowData.converted_to_po ? 'Converted to PO' : 'Available' },
            { title: 'Org-Des', field: 'org_des', render: (rowData: any) => `${rowData.org || rowData.origin || ''}-${rowData.dst || rowData.destination || ''}` },
            { title: 'Flight Number', field: 'flight_no', render: (rowData: any) => {
                if (rowData.flight) return rowData.flight.trim();
                if (rowData.parts && rowData.parts.length > 0) return rowData.parts[0].flight_no || '';
                return rowData.flight_no || (rowData.parentOrder && rowData.parentOrder.flight ? rowData.parentOrder.flight : '');
            } },
            { title: 'Arrv Date', field: 'arrival_date', render: (rowData: any) => {
                let dt = rowData.arrival_date;
                if (!dt && rowData.parts && rowData.parts.length > 0) dt = rowData.parts[0].arrival_date;
                if (!dt && rowData.parentOrder && rowData.parentOrder.arrival_date) dt = rowData.parentOrder.arrival_date;
                return dt ? Moment(dt).format('DD MMM YYYY').toUpperCase() : '';
            }},
            { title: 'Driver Check In', field: 'driver_check_in', render: (rowData: any) => {
                const dt = rowData.parentOrder && rowData.parentOrder.driver_checkin_time ? rowData.parentOrder.driver_checkin_time : null;
                return dt && dt.length >= 4 ? `${dt.substring(0, 2)}:${dt.substring(2, 4)}` : (dt || '');
            } },
            { title: 'Driver Check Out', field: 'driver_check_out', render: (rowData: any) => {
                const dt = rowData.parentOrder && rowData.parentOrder.delivered_time ? rowData.parentOrder.delivered_time : null;
                return dt && dt.length >= 4 ? `${dt.substring(0, 2)}:${dt.substring(2, 4)}` : (dt || '');
            } },
            { title: 'Ind', field: 'ind', render: (rowData: any) => {
                const colorStatus = rowData.color_status || (rowData.parentOrder && rowData.parentOrder.color_status ? rowData.parentOrder.color_status : 'green');
                const suspended = rowData.parentOrder && rowData.parentOrder.suspended;
                const colorClass = suspended ? classes.yellow : (classes as any)[colorStatus] || classes.green;
                return <DotIcon className={colorClass} />;
            } },
            { title: 'Truck Status', field: 'truck_status', render: (rowData: any) => {
                return rowData.parentOrder && rowData.parentOrder.truck_status ? truckStatusTime(rowData.parentOrder as any) : '';
            } }
          ]}
          isLoading={loading}
          options={{
            selection: true,
            paging: false,
            selectionProps: (rowData: any) => ({
              color: 'primary',
              size: 'small',
              disabled: !!rowData.converted_to_po,
            }),
            searchFieldAlignment: 'right',
            searchFieldStyle: {
              borderRadius: '16px',
              padding: '2px 10px',
              fontSize: '13px',
              backgroundColor: 'rgb(255, 255, 255)',
              minWidth: '240px',
            },
            maxBodyHeight: '370px',
            headerStyle: {
              backgroundColor: 'var(--tbl_head_bkg)',
              color: 'var(--tbl_head_color)',
              zIndex: 2,
            },
            showTextRowsSelected: false,
          }}
          onSelectionChange={(rows: any) => onSelectionChange((rows || []).filter((row: any) => !row.converted_to_po))}
          title={<h3 className={ `${ commonStyles.header }` } >Global Available AWBs (from GOs)</h3>}
        />
      </div>
    </div>
  );
}
